/**
 * AgentsDiagnosisPanel — Diagnostica e "unione" (merge) di agenti duplicati.
 *
 * Risolve il bug bulk-import: se l'admin importa clienti con la colonna `agent_name`
 * e il nome nel CSV non matcha PERFETTAMENTE il nome dell'agente esistente, il sistema
 * crea in silenzio un agente "orfano" (auto_created=true, senza login) e assegna i
 * clienti a quello. L'agente vero (con login) vede quindi zero clienti.
 *
 * Questo pannello:
 *  1. mostra tutti gli agenti con badge stato (has_login, auto_created, counters)
 *  2. rileva duplicati per nome normalizzato (accenti/spazi/case)
 *  3. permette il MERGE: sposta customers/orders/offers da source → target
 *  4. permette la pulizia in massa degli orfani senza clienti
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Users, Shield, Wand2, Trash2, ChevronDown, ChevronRight, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { API } from "../utils/format";

const badgeStyles = "text-[10px] uppercase font-semibold tracking-wide";

export default function AgentsDiagnosisPanel({ onDataChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mergeSource, setMergeSource] = useState(null); // agent object
  const [mergeTargetId, setMergeTargetId] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/agents/diagnosis`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricamento diagnostica");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); }, []);

  const agents = data?.agents || [];
  const totals = data?.totals || {};
  const duplicates = data?.duplicates_by_name || [];

  const hasIssues = useMemo(() => (
    (totals.orphans || 0) > 0 ||
    (totals.duplicate_names || 0) > 0 ||
    (totals.orphan_customers_to_reassign || 0) > 0
  ), [totals]);

  const agentsById = useMemo(() => Object.fromEntries(agents.map((a) => [a.id, a])), [agents]);

  // Suggested merge target for a given source: prefer agent with same normalized name that HAS login and NO auto_created flag
  const suggestTargetFor = (source) => {
    const dupGroup = duplicates.find((d) => (d.agent_ids || []).includes(source.id));
    if (!dupGroup) return "";
    const others = dupGroup.agent_ids.filter((id) => id !== source.id).map((id) => agentsById[id]).filter(Boolean);
    // Prefer: has_login=true, auto_created=false, most customers
    others.sort((a, b) => (
      (b.has_login - a.has_login) ||
      ((!!a.auto_created) - (!!b.auto_created)) ||
      (b.active_customers_count - a.active_customers_count)
    ));
    return others[0]?.id || "";
  };

  const openMerge = (source) => {
    setMergeSource(source);
    setMergeTargetId(suggestTargetFor(source) || "");
  };

  const doMerge = async () => {
    if (!mergeSource || !mergeTargetId) return;
    const target = agentsById[mergeTargetId];
    if (!target) return;
    if (!window.confirm(
      `Unire "${mergeSource.name}" (${mergeSource.access_code}) → "${target.name}" (${target.access_code})?\n\n` +
      `${mergeSource.active_customers_count} clienti · ${mergeSource.orders_count} ordini · ${mergeSource.offers_count} offerte verranno spostati.\n` +
      `L'agente "${mergeSource.name}" (${mergeSource.access_code}) verrà eliminato definitivamente.\n\n` +
      `Confermare?`
    )) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/agents/${mergeSource.id}/merge-into/${mergeTargetId}`);
      toast.success(`Uniti: ${r.data.moved.customers} clienti, ${r.data.moved.orders} ordini, ${r.data.moved.dedicated_offers} offerte spostati`);
      setMergeSource(null);
      setMergeTargetId("");
      await fetchData();
      onDataChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'unione");
    } finally {
      setBusy(false);
    }
  };

  const cleanupOrphans = async () => {
    const count = (data?.orphans_auto_created_zero_customers || []).length;
    if (count === 0) return;
    if (!window.confirm(`Eliminare ${count} agenti auto-creati SENZA clienti/ordini/offerte?\nOperazione sicura: non tocca dati collegati.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/agents/cleanup-orphans`);
      toast.success(`Eliminati ${r.data.deleted_count} agenti orfani vuoti`);
      await fetchData();
      onDataChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore pulizia");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-stone-50 border-stone-200" data-testid="agents-diagnosis-loading">
        <CardContent className="p-4 flex items-center gap-2 text-sm text-stone-500">
          <Loader2 className="w-4 h-4 animate-spin" />Diagnostica agenti in corso...
        </CardContent>
      </Card>
    );
  }

  if (!hasIssues && agents.length > 0) {
    // Tutto ok — mostra un badge compatto
    return (
      <Card className="bg-emerald-50 border-emerald-200" data-testid="agents-diagnosis-ok">
        <CardContent className="p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-emerald-800">
            <CheckCircle2 className="w-4 h-4" />
            <span className="font-medium">Tutti gli agenti sono in ordine.</span>
            <span className="text-emerald-600">{totals.agents} agenti · tutti con login · 0 duplicati</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="text-emerald-700 hover:bg-emerald-100" data-testid="agents-diagnosis-toggle">
            {expanded ? "Nascondi" : "Vedi dettaglio"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-2 ${hasIssues ? "border-amber-300 bg-amber-50/50" : "border-stone-200"}`} data-testid="agents-diagnosis-panel">
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
                Diagnostica Agenti — {hasIssues ? "Problemi rilevati" : "Tutto ok"}
              </h3>
              <div className="text-xs text-stone-600 flex flex-wrap gap-x-3 gap-y-1 mt-0.5">
                <span>{totals.agents} agenti</span>
                {(totals.duplicate_names || 0) > 0 && (
                  <span className="text-amber-700 font-medium">⚠ {totals.duplicate_names} nomi duplicati</span>
                )}
                {(totals.orphans || 0) > 0 && (
                  <span className="text-amber-700 font-medium">⚠ {totals.orphans} senza login</span>
                )}
                {(totals.orphan_customers_to_reassign || 0) > 0 && (
                  <span className="text-rose-700 font-semibold">🚨 {totals.orphan_customers_to_reassign} clienti su agenti senza login</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {(data?.orphans_auto_created_zero_customers || []).length > 0 && (
              <Button size="sm" variant="outline" onClick={cleanupOrphans} disabled={busy} className="border-stone-300 text-stone-700" data-testid="agents-cleanup-orphans-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Pulisci {(data.orphans_auto_created_zero_customers || []).length} orfani vuoti
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setExpanded(!expanded)} data-testid="agents-diagnosis-toggle">
              {expanded ? <ChevronDown className="w-3.5 h-3.5 mr-1" /> : <ChevronRight className="w-3.5 h-3.5 mr-1" />}
              {expanded ? "Nascondi" : "Vedi dettaglio"}
            </Button>
          </div>
        </div>

        {/* Duplicati groups */}
        {expanded && duplicates.length > 0 && (
          <div className="border-t border-amber-200 pt-3 space-y-3" data-testid="agents-duplicates-list">
            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Nomi duplicati ({duplicates.length})</p>
            {duplicates.map((grp) => {
              const groupAgents = grp.agent_ids.map((id) => agentsById[id]).filter(Boolean);
              return (
                <div key={grp.normalized_name} className="bg-white border border-amber-200 rounded-lg p-3 space-y-2">
                  <p className="text-sm font-medium text-stone-900">
                    &laquo;{grp.normalized_name}&raquo; · <span className="text-amber-700">{grp.count} varianti</span>
                  </p>
                  <div className="space-y-1.5">
                    {groupAgents.map((a) => (
                      <AgentRow key={a.id} agent={a} onMerge={() => openMerge(a)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Orphans (with clients but no login) */}
        {expanded && (
          <div className="border-t border-amber-200 pt-3 space-y-2" data-testid="agents-orphans-list">
            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">
              Agenti senza login {(totals.orphans || 0) > 0 ? `(${totals.orphans})` : ""}
            </p>
            {agents.filter((a) => !a.has_login).length === 0 && (
              <p className="text-xs text-stone-500 italic">Nessuno.</p>
            )}
            {agents.filter((a) => !a.has_login).map((a) => (
              <AgentRow key={a.id} agent={a} onMerge={() => openMerge(a)} highlight={a.active_customers_count > 0} />
            ))}
          </div>
        )}

        {/* Merge dialog inline */}
        {mergeSource && (
          <div className="border-2 border-[#8A1C2A] bg-white rounded-lg p-4 space-y-3" data-testid="agents-merge-dialog">
            <div className="flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-[#8A1C2A]" />
              <p className="text-sm font-semibold text-stone-900">Unisci agente</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
              <div className="bg-rose-50 border border-rose-200 rounded p-3">
                <p className="text-[10px] uppercase font-semibold text-rose-700 tracking-wide mb-1">Da (verrà eliminato)</p>
                <p className="font-semibold text-stone-900 text-sm truncate">{mergeSource.name}</p>
                <p className="text-xs text-stone-500 font-mono">{mergeSource.access_code}</p>
                <p className="text-xs text-stone-600 mt-1">
                  {mergeSource.active_customers_count} clienti · {mergeSource.orders_count} ordini · {mergeSource.offers_count} offerte
                </p>
              </div>
              <ArrowRight className="w-6 h-6 text-stone-400 mx-auto" />
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                <p className="text-[10px] uppercase font-semibold text-emerald-700 tracking-wide mb-1">A (destinazione)</p>
                <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
                  <SelectTrigger className="bg-white" data-testid="agents-merge-target-select">
                    <SelectValue placeholder="Seleziona agente destinazione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.filter((a) => a.id !== mergeSource.id).map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.has_login ? "🟢 " : "🟡 "}{a.name} · {a.access_code} · {a.active_customers_count} cli.
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {mergeTargetId && agentsById[mergeTargetId] && (
                  <p className="text-xs text-emerald-700 mt-1">
                    ✓ {agentsById[mergeTargetId].has_login ? "Ha login attivo" : "⚠ Nessun login"}
                    · {agentsById[mergeTargetId].active_customers_count} clienti attuali
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => setMergeSource(null)} disabled={busy}>
                Annulla
              </Button>
              <Button
                size="sm"
                onClick={doMerge}
                disabled={busy || !mergeTargetId}
                className="bg-[#8A1C2A] hover:bg-[#6e1622] text-white"
                data-testid="agents-merge-confirm-btn"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
                Unisci ed elimina origine
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function AgentRow({ agent, onMerge, highlight = false }) {
  return (
    <div
      className={`flex flex-wrap items-center gap-2 rounded px-3 py-2 border ${highlight ? "border-rose-300 bg-rose-50" : "border-stone-200 bg-stone-50"}`}
      data-testid={`agent-row-${agent.id}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-stone-900 truncate">
          {agent.name}
          <span className="text-stone-500 font-mono text-xs ml-2">{agent.access_code}</span>
        </p>
        <div className="flex flex-wrap gap-1 mt-0.5">
          {agent.has_login ? (
            <Badge variant="outline" className={`${badgeStyles} bg-emerald-50 text-emerald-700 border-emerald-300`}>
              <Shield className="w-3 h-3 mr-0.5 inline" />USER OK
              {agent.user_email && <span className="ml-1 opacity-60">· {agent.user_email}</span>}
            </Badge>
          ) : (
            <Badge variant="outline" className={`${badgeStyles} bg-amber-50 text-amber-700 border-amber-300`}>
              ⚠ NO LOGIN
            </Badge>
          )}
          {agent.auto_created && (
            <Badge variant="outline" className={`${badgeStyles} bg-stone-100 text-stone-600 border-stone-300`}>
              AUTO-CREATO
            </Badge>
          )}
          <Badge variant="outline" className={`${badgeStyles} bg-stone-50 text-stone-700 border-stone-300`}>
            <Users className="w-3 h-3 mr-0.5 inline" />{agent.active_customers_count} cli.
          </Badge>
          {agent.orders_count > 0 && (
            <Badge variant="outline" className={`${badgeStyles} bg-stone-50 text-stone-700 border-stone-300`}>
              {agent.orders_count} ord.
            </Badge>
          )}
          {agent.offers_count > 0 && (
            <Badge variant="outline" className={`${badgeStyles} bg-stone-50 text-stone-700 border-stone-300`}>
              {agent.offers_count} off.
            </Badge>
          )}
        </div>
      </div>
      <Button size="sm" variant="outline" onClick={onMerge} className="text-[#8A1C2A] border-[#8A1C2A]/40 hover:bg-[#8A1C2A]/5" data-testid={`agent-merge-btn-${agent.id}`}>
        <Wand2 className="w-3.5 h-3.5 mr-1" />Unisci in...
      </Button>
    </div>
  );
}
